import { srpFilterInitialValues, useSrpFilters } from 'lib/hooks/useSrpFilters';
import {
  SrpFilterName,
  SrpBodyTypesFilterValue,
  MakeModel,
  SrpBodyTypeName,
} from 'types/srpFilters';
import { useEffect, useState, useCallback, useContext } from 'react';
import { TextSearchValue } from 'components/Shared/TextSearch';
import { useTranslation } from 'react-i18next';
import { AdGlobalRefreshContext } from 'contexts/AdGlobalRefreshContext';

export type UseSearchBarModelResult = [TextSearchValue | undefined, SearchBarChangeHandler];

export type SearchBarChangeHandler = (val: TextSearchValue) => void;

export function useSearchBarModel(): UseSearchBarModelResult {
  const { t } = useTranslation();
  const filters = useSrpFilters();
  const makeModel = filters.values[SrpFilterName.MakeModel];
  const bodyTypes = filters.values[SrpFilterName.BodyTypes];

  const { refreshGlobalAds } = useContext(AdGlobalRefreshContext);

  const translateBodyType = useCallback((filterBodyType: string): string => {
    return t(`srp.filters.bodyStyles.${filterBodyType}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [value, setValue] = useState<TextSearchValue>();

  useEffect(() => {
    setValue(
      getTextSearchValue(
        {
          bodyTypes,
          makeModel,
        },
        translateBodyType
      )
    );
  }, [makeModel, bodyTypes, translateBodyType]);

  const handleChange = (val: TextSearchValue): void => {
    const bodyTypesValue: SrpBodyTypesFilterValue = {
      ...srpFilterInitialValues[SrpFilterName.BodyTypes],
    };

    if (val.bodyType) {
      bodyTypesValue[val.bodyType as SrpBodyTypeName] = true;
    }

    refreshGlobalAds();

    filters.setValues({
      [SrpFilterName.MakeModel]: {
        make: val.make,
        model: val.model,
      },
      [SrpFilterName.BodyTypes]: bodyTypesValue,
      [SrpFilterName.Trims]: [],
    });
  };

  return [value, handleChange];
}

interface GetValueParams {
  bodyTypes: SrpBodyTypesFilterValue;
  makeModel: MakeModel;
}

function getTextSearchValue(
  filterState: GetValueParams,
  translateBodyType: (filterBodyType: string) => string
): TextSearchValue {
  const emptyTextSearchValue: TextSearchValue = {
    make: undefined,
    model: undefined,
    bodyType: undefined,
  };

  const bodyTypes = Object.entries(filterState.bodyTypes).reduce<string[]>((acc, [name, value]) => {
    if (value) {
      acc.push(name);
    }
    return acc;
  }, []);

  if (bodyTypes.length > 1) {
    return emptyTextSearchValue;
  }

  if (!filterState.makeModel.make) {
    const bodyType = bodyTypes[0];
    const translatedBodyType = bodyType && translateBodyType(bodyType);
    return {
      ...emptyTextSearchValue,
      bodyType,
      searchText: translatedBodyType,
    };
  }

  if (filterState.makeModel.make) {
    if (bodyTypes.length > 0) {
      return emptyTextSearchValue;
    }

    return {
      ...emptyTextSearchValue,
      ...filterState.makeModel,
      searchText: `${filterState.makeModel?.make} ${filterState.makeModel?.model || ''}`,
    };
  }

  return emptyTextSearchValue;
}
